
import Vue from "vue";
import axiosBase from "axios";
import { showNotifyDialog } from "@/lib/dialog";
import { Carousel, Slide } from "vue-carousel";
import store from "@/store";
import { User, Novel, NovelFromNolaNovel } from "@/lib/models";
import { evaluateBanner, validateBasicConditions, filterBanners } from "@/lib/utils/banner";
import { optimizeImage } from "@/lib/utils/url";
import commerceBannerImage from "@/assets/img/static/banner-commerce-debut.webp";

const axios = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { Carousel, Slide },
  data() {
    return {
      // microCMSから取得したバナーデータを格納するプロパティ
      // microCMS側でプロパティが変更される場合があるため、any型で定義
      banners: [] as any[],
    };
  },
  computed: {
    novels(): Novel[] {
      return store.getters["novelModule/novels"];
    },
    user(): User {
      return store.getters["userModule/user"];
    },
    novelsFromNolaNovel(): NovelFromNolaNovel[] {
      return store.getters["nolaNovelModule/novels"];
    },
    currentPage(): string {
      return this.$route.path;
    },
    hasUsedCommerceBanner(): boolean {
      return store.getters["generalModule/hasUsedCommerceBanner"];
    },
  },
  async mounted() {
    await this.fetchAndProcessBanners();
  },
  methods: {
    optimizeImage(url: string): string {
      return optimizeImage(url);
    },
    async getPrioritizedBanners(banners: any[]) {
      // 基本条件に合致しないバナーを除外
      const validBanners = await Promise.all(
        banners.map(async (banner) => ({
          banner,
          isValid: await validateBasicConditions(banner, this.currentPage),
        }))
      );

      const filteredBanners = validBanners.filter((result) => result.isValid).map((result) => result.banner);

      // 条件付きバナーと条件無しバナーを分類
      const conditionalBanners = filteredBanners.filter(
        (banner) => banner.operator?.length > 0 && banner.conditions?.length > 0
      );
      const unconditionalBanners = filteredBanners.filter(
        (banner) => !banner.operator?.length || !banner.conditions?.length
      );

      // 条件付きバナーを評価
      const evaluatedBanners = await Promise.all(
        conditionalBanners.map(async (banner) => ({
          banner,
          isValid: evaluateBanner(banner, this.novels, this.novelsFromNolaNovel, this.user),
        }))
      );

      // 条件にマッチしたバナーを優先して条件無しバナーと統合
      const matchedBanners = evaluatedBanners.filter((result) => result.isValid).map((result) => result.banner);
      return [...matchedBanners, ...unconditionalBanners];
    },
    handleCommerceBannerClick() {
      if (!this.hasUsedCommerceBanner) {
        this.$router.push("/commerce-debut-guide");
        // 商業道バナーを初めてクリックした場合、利用済みフラグを立てる
        store.dispatch("generalModule/setUsedCommerceBanner", true);
      }
    },
    async fetchAndProcessBanners() {
      try {
        const bannerResponse = await axios.get(`/bannerweb`);
        if (bannerResponse.status !== 200) {
          await showNotifyDialog({
            title: "エラー",
            content: "バナーの取得に失敗しました。",
          });
          return;
        }
        // 優先して表示するバナーの降順でバナーの配列を取得
        let prioritizedBanners = await this.getPrioritizedBanners(bannerResponse.data.contents);

        if (this.hasUsedCommerceBanner) {
          // 利用済みフラグが立っている場合、商業道へのバナーを除外
          prioritizedBanners = filterBanners(prioritizedBanners, (banner) =>
            banner.id.includes("banner-commerce-debut")
          );
        } else {
          const bannerCommerceDebut = {
            id: "banner-commerce-debut",
            image: {
              url: commerceBannerImage,
            },
          };

          // 商業道バナーを配列の先頭に追加
          prioritizedBanners.unshift(bannerCommerceDebut);
        }

        this.banners = prioritizedBanners;
      } catch (error) {
        await showNotifyDialog({
          title: "エラー",
          content: "通信エラーが発生しました。",
        });
        console.error(error);
      }
    },
  },
});

interface Props {}

interface Data {
  banners: any[];
}

interface Computed {
  novels: Novel[];
  user: User;
  novelsFromNolaNovel: NovelFromNolaNovel[];
  currentPage: string;
  hasUsedCommerceBanner: boolean;
}

interface Methods {
  optimizeImage(url: string): string;
  getPrioritizedBanners(banners: any[]): Promise<any[]>;
  handleCommerceBannerClick(): void;
  fetchAndProcessBanners(): Promise<void>;
}
